import { CommonModule } from '@angular/common';
import { Component, DestroyRef, HostListener, inject, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import type { CompanySignupDraftDTO } from '@libs/dtos/merit/freemium.dto';
import { PROPERTY_TYPES } from '@libs/dtos/merit/merit.catalogs';
import { INTERNATIONAL_PHONE_INPUT_REGEX, normalizePhone } from '@libs/dtos/merit/phone.util';
import { AnalyticsService } from '@services/analytics/analytics.service';
import { FreemiumService } from '@services/merit/freemium.service';
import { MeritConfigService } from '@services/merit/merit-config.service';
import { MeritI18nService } from '@services/merit/merit-i18n.service';
import { SelectComponent } from '@shared/components/select/select.component';
import {
	asyncScheduler,
	catchError,
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	of,
	switchMap,
	throttleTime,
} from 'rxjs';

/**
 * Cada cuánto se guarda el borrador, y por qué estos dos números.
 *
 * `DRAFT_DEBOUNCE_MS` — se espera a que la persona **pare de escribir** 2,5 s.
 * Guardar en cada tecla convertiría el formulario en una tormenta de peticiones
 * (un nombre de empresa son veinte), y guardar «al salir de cada campo» pierde
 * justo el caso que estos flujos vienen a atender: quien escribe medio campo y
 * se va sin pasar al siguiente.
 *
 * `DRAFT_MIN_INTERVAL_MS` — y pase lo que pase, no más de un guardado cada 15 s.
 * Es el techo: alguien que escriba, pare, escriba, pare durante cinco minutos
 * hace 20 peticiones en vez de 120. Lo último tecleado nunca se pierde porque el
 * acelerador conserva la última emisión (`trailing`), y lo que quede por guardar
 * al cerrar la pestaña sale por `sendBeacon`.
 */
const DRAFT_DEBOUNCE_MS = 2500;
const DRAFT_MIN_INTERVAL_MS = 15000;

/**
 * Dónde se recuerda el enlace del borrador en ESTE navegador, para que volver a
 * abrir el formulario recupere lo escrito sin depender del correo. Es el
 * dispositivo de la propia persona, y se borra al terminar el alta.
 */
const DRAFT_STORAGE_KEY = 'kairos.companySignupDraft';

/**
 * Alta gratuita de empresa (`MD-Freemium-Empresa §3`). Vive **fuera del login**:
 * se enlaza desde la web y desde la propia plataforma, y no hay guard porque no
 * hay nada que proteger — quien lo rellena todavía no tiene cuenta.
 *
 * Al enviar NO se entra: la respuesta solo dice a qué dirección se ha escrito.
 * La contraseña la pone la persona desde el enlace del correo, que es también lo
 * que confirma que la dirección existe. Meter aquí un "entrar ahora" convertiría
 * un formulario público en una forma de acceder sin pasar por el buzón.
 */
@Component({
	selector: 'app-company-signup',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, RouterLink, TranslocoModule, SelectComponent],
	templateUrl: './company-signup.component.html',
	styleUrl: './company-signup.component.scss',
})
export class CompanySignupComponent implements OnInit {
	private readonly fb = inject(FormBuilder);
	private readonly api = inject(FreemiumService);
	private readonly analytics = inject(AnalyticsService);

	/**
	 * Logo o imagen de la propiedad (08/09), opcional. Se sube en cuanto se
	 * elige —el alta no lleva ficheros— y el alta guarda la ruta. Un `File` no
	 * cabe en el borrador, así que al retomar hay que volver a elegirlo.
	 */
	readonly logoPath = signal<string | null>(null);
	readonly logoPreview = signal<string | null>(null);
	readonly logoBusy = signal(false);
	readonly logoError = signal<'tooLarge' | 'notImage' | 'generic' | null>(null);

	onLogoPicked(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		input.value = '';
		if (!file) return;
		this.logoError.set(null);
		if (!file.type.startsWith('image/')) {
			this.logoError.set('notImage');
			return;
		}
		if (file.size > 2 * 1024 * 1024) {
			this.logoError.set('tooLarge');
			return;
		}
		this.logoBusy.set(true);
		this.api.uploadSignupLogo(file).subscribe({
			next: ({ logoPath }) => {
				this.logoBusy.set(false);
				this.logoPath.set(logoPath);
				this.logoPreview.set(URL.createObjectURL(file));
			},
			error: (err: { error?: { message?: string } }) => {
				this.logoBusy.set(false);
				const code = err?.error?.message;
				this.logoError.set(
					code === 'LOGO_TOO_LARGE' ? 'tooLarge' : code === 'LOGO_MUST_BE_IMAGE' ? 'notImage' : 'generic',
				);
			},
		});
	}

	removeLogo(): void {
		this.logoPath.set(null);
		this.logoPreview.set(null);
		this.logoError.set(null);
	}
	private readonly config = inject(MeritConfigService);
	private readonly route = inject(ActivatedRoute);
	private readonly destroyRef = inject(DestroyRef);
	readonly meritI18n = inject(MeritI18nService);

	readonly submitting = signal(false);
	readonly done = signal(false);
	/** Dónde se ha escrito, para la pantalla de "revisa tu correo". */
	readonly sentTo = signal<string | null>(null);
	readonly serverError = signal<string | null>(null);
	/** El aviso legal, el mismo que ofrece el login: el consentimiento hay que poder leerlo. */

	/** Se ha recuperado un alta a medias: hay que decirlo, o parece cosa de magia. */
	readonly draftResumed = signal(false);
	/** Última vez que se guardó el borrador, para que se vea que está a salvo. */
	readonly draftSavedAt = signal<Date | null>(null);

	/** El enlace del borrador vivo. Nulo hasta el primer guardado. */
	private draftToken: string | null = null;
	/** Lo último que se mandó, para no repetir una petición idéntica. */
	private lastSavedSnapshot = '';

	propertyTypeOptions: { label: string; value: string }[] = [];

	readonly form = this.fb.nonNullable.group({
		companyName: ['', [Validators.required, Validators.minLength(2)]],
		contactName: ['', [Validators.required, Validators.minLength(2)]],
		email: ['', [Validators.required, Validators.email]],
		/**
		 * Teléfono y puesto son obligatorios desde el 25/08: son dos de los cinco
		 * campos de los que sale el criterio «acceso al decisor» (10 puntos), y
		 * ese criterio solo mira si están rellenos.
		 *
		 * El teléfono además lleva formato. Obligarlo como texto libre habría
		 * subido la nota de todas las altas sin subir la información: un «llamar
		 * por las tardes» puntúa igual que un número. El patrón admite los
		 * separadores que la gente teclea porque valida sobre el input tal cual;
		 * quien decide de verdad es el mismo `phone.util` en el servidor.
		 */
		phone: ['', [Validators.required, Validators.pattern(INTERNATIONAL_PHONE_INPUT_REGEX)]],
		position: ['', [Validators.required, Validators.minLength(2)]],
		propertyType: [''],
		city: [''],
		website: [''],
		// Mismo consentimiento PDPL que el formulario comercial: sin él no se envía.
		privacy: [false, [Validators.requiredTrue]],
	});

	ngOnInit(): void {
		this.loadPropertyTypes();
		this.resumeDraft();
		this.autosaveDraft();
	}

	/**
	 * Los tipos de negocio salen del catálogo que edita el admin. Si la llamada
	 * falla se usa la constante del código —los mismos valores con los que se
	 * sembró— para que el formulario siga sirviendo: es la única puerta de alta y
	 * dejarlo sin opciones lo dejaría inservible.
	 */
	private loadPropertyTypes(): void {
		const toOptions = (values: readonly string[]) => this.meritI18n.options(values);
		this.propertyTypeOptions = toOptions(PROPERTY_TYPES);
		this.config.listCatalog('property_types').subscribe({
			next: (items) => {
				if (items.length) this.propertyTypeOptions = toOptions(items.map((item) => item.label));
			},
			error: () => {
				/* se queda la constante */
			},
		});
	}

	/**
	 * Escape cierra el aviso legal. Un diálogo del que solo se sale con el ratón
	 * deja fuera a quien navega con teclado, que es justo quien más lo necesita.
	 */
	@HostListener('document:keydown.escape')
	closeLegal(): void {}

	submit(): void {
		// `done()` too: after the account exists there is nothing left to send, and a
		// second pass would push a second `sign_up` for the same company.
		if (this.form.invalid || this.submitting() || this.done()) {
			this.form.markAllAsTouched();
			return;
		}
		this.submitting.set(true);
		this.serverError.set(null);

		const value = this.form.getRawValue();
		const trimmed = (input: string) => (input.trim() ? input.trim() : null);
		this.api
			.signup({
				companyName: value.companyName.trim(),
				contactName: value.contactName.trim(),
				email: value.email.trim(),
				// Se manda ya sin separadores. El servidor lo vuelve a normalizar —es
				// él quien manda—, pero mandarlo tal cual dejaría que el mismo
				// teléfono llegara escrito de cinco formas según quién lo teclee.
				phone: normalizePhone(value.phone) ?? '',
				position: value.position.trim(),
				propertyType: trimmed(value.propertyType),
				city: trimmed(value.city),
				website: trimmed(value.website),
				logoPath: this.logoPath(),
				privacy: true,
				// Qué puerta se ha usado. El formulario es el mismo; saber de dónde
				// llega la gente es lo que dice si el enlace de la web sirve.
				origin: this.origin(),
				// Cierra exactamente el borrador del que sale este envío y retira sus
				// avisos de «alta a medias». Sin esto, quien acaba de crear su cuenta
				// recibe a las 24 h un «te falta terminar».
				draftToken: this.draftToken,
			})
			.subscribe({
				next: (result) => {
					this.sentTo.set(result.email);
					this.done.set(true);
					// The account exists now, and only now: `sign_up` goes out on the
					// server's 2xx, so a 409 (address taken), a failed request or a
					// double click that the server refuses never counts as a signup.
					this.analytics.companySignedUp(this.origin());
					this.submitting.set(false);
					// El borrador ya no lleva a ninguna parte: se olvida el enlace en este
					// navegador. En el servidor lo cierra el propio alta.
					this.draftToken = null;
					this.forgetDraftToken();
				},
				error: (error: { error?: { message?: string } }) => {
					// Tres finales distintos, y el del medio es el que faltaba: una
					// cuenta dada de baja conserva el correo pero no puede entrar,
					// así que mandarla al login era pasearla en círculos.
					const code = String(error?.error?.message ?? '');
					this.serverError.set(
						code === 'EMAIL_ALREADY_REGISTERED'
							? 'emailTaken'
							: code === 'ACCOUNT_DEACTIVATED'
								? 'accountDeactivated'
								: 'generic',
					);
					this.submitting.set(false);
				},
			});
	}

	// ─────────────────────────── El borrador del alta ───────────────────────────
	//
	// PDPL, y es la decisión de fondo de todo este bloque: **no se guarda ni un
	// campo antes de que la casilla esté marcada**. El documento avisa de que la
	// web manda hoy el contacto incompleto al CRM ANTES del consentimiento, y de
	// que con la ley de Emiratos eso hay que mirarlo. Aquí, mientras no hay
	// consentimiento lo tecleado NO sale del navegador: `draftFields()` devuelve
	// null y no se llama a nada. Marcar la casilla es lo que autoriza a guardar y
	// a escribir, y el formulario lo dice justo al lado — consentir a escondidas
	// no es consentir.

	/**
	 * Recupera un alta a medias: por el enlace del correo (`?draft=`) o por el que
	 * este navegador recuerde.
	 *
	 * Un token caducado o de un alta ya terminada se olvida sin ruido: quien llega
	 * a un formulario en blanco no necesita un error, necesita el formulario.
	 */
	private resumeDraft(): void {
		const token = this.route.snapshot.queryParamMap.get('draft') ?? this.storedDraftToken();
		if (!token) return;

		this.api.resumeDraft(token).subscribe({
			next: (draft) => {
				const data = draft.data as Record<string, string>;
				this.form.patchValue({
					companyName: data['companyName'] ?? '',
					contactName: data['contactName'] ?? '',
					email: data['email'] ?? '',
					phone: data['phone'] ?? '',
					position: data['position'] ?? '',
					propertyType: data['propertyType'] ?? '',
					city: data['city'] ?? '',
					website: data['website'] ?? '',
					// Se recupera marcada porque el borrador SOLO existe si se aceptó:
					// volver a pedirla daría a entender que la vez anterior no valió.
					privacy: true,
				});
				this.draftToken = token;
				this.rememberDraftToken(token);
				this.draftResumed.set(true);
				this.lastSavedSnapshot = JSON.stringify(this.draftFields());
			},
			error: () => this.forgetDraftToken(),
		});
	}

	/**
	 * Guarda solo cuando hay algo nuevo y como mucho una vez cada
	 * `DRAFT_MIN_INTERVAL_MS`. Ver la nota de las dos constantes: el debounce
	 * espera a que la persona pare de escribir, y el acelerador pone el techo de
	 * peticiones sin perder lo último tecleado (`trailing`).
	 */
	private autosaveDraft(): void {
		this.form.valueChanges
			.pipe(
				debounceTime(DRAFT_DEBOUNCE_MS),
				map(() => this.draftFields()),
				// Sin consentimiento no hay borrador; con el alta ya enviada, tampoco.
				filter((fields): fields is CompanySignupDraftDTO => fields !== null),
				map((fields) => JSON.stringify(fields)),
				// Volver atrás y dejarlo como estaba no es un cambio que guardar.
				distinctUntilChanged(),
				// Y recuperar un borrador tampoco: rellenar el formulario dispara
				// `valueChanges`, y sin esto lo primero que haría al volver sería
				// guardar exactamente lo que acaba de leer.
				filter((snapshot) => snapshot !== this.lastSavedSnapshot),
				throttleTime(DRAFT_MIN_INTERVAL_MS, asyncScheduler, { leading: true, trailing: true }),
				switchMap((snapshot) => this.persistDraft(snapshot)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();
	}

	/**
	 * Último intento al cerrar la pestaña.
	 *
	 * Es el caso que describe el documento —«escribió algunos campos y se fue»— y
	 * el que el debounce no alcanza: quien teclea y cierra antes de 2,5 s perdería
	 * lo escrito. `pagehide` y no `beforeunload` porque el segundo no se dispara en
	 * el Safari de iOS ni cuando la pestaña se guarda en el caché de navegación.
	 */
	@HostListener('window:pagehide')
	flushDraft(): void {
		const fields = this.draftFields();
		if (!fields) return;
		if (JSON.stringify(fields) === this.lastSavedSnapshot) return;
		this.api.saveDraftOnUnload({ ...fields, token: this.draftToken });
	}

	/** Manda el borrador. Un fallo no puede romper el formulario. */
	private persistDraft(snapshot: string) {
		const fields = JSON.parse(snapshot) as CompanySignupDraftDTO;
		return this.api.saveDraft({ ...fields, token: this.draftToken }).pipe(
			map((saved) => {
				this.draftToken = saved.token;
				this.rememberDraftToken(saved.token);
				this.lastSavedSnapshot = snapshot;
				this.draftSavedAt.set(new Date(saved.savedAt));
				return saved;
			}),
			// El alta es lo importante; el borrador es la red. Un error aquí se traga
			// a propósito: avisar de que «no se ha podido guardar el borrador» asusta
			// sobre algo que la persona no ha pedido y no puede arreglar.
			catchError(() => of(null)),
		);
	}

	/**
	 * Lo que hay escrito, o `null` si todavía no se puede guardar.
	 *
	 * El token NO va aquí: se añade al mandar. Si formara parte de la foto, el
	 * primer guardado —que es el que lo estrena— se vería como un cambio y
	 * dispararía otro guardado detrás.
	 */
	private draftFields(): CompanySignupDraftDTO | null {
		if (this.done()) return null;
		const value = this.form.getRawValue();
		if (value.privacy !== true) return null;

		const text = (input: string) => (input.trim() ? input.trim() : undefined);
		const fields: CompanySignupDraftDTO = {
			companyName: text(value.companyName),
			contactName: text(value.contactName),
			email: text(value.email),
			phone: text(value.phone),
			position: text(value.position),
			propertyType: text(value.propertyType),
			city: text(value.city),
			website: text(value.website),
			origin: this.origin(),
			privacy: true,
		};
		// Aceptar la casilla y no haber escrito nada todavía no es un borrador: no
		// hay a quién llamar ni a quién escribir, y crearía un aviso vacío.
		const written = Object.entries(fields).some(
			([key, entry]) => key !== 'privacy' && key !== 'origin' && entry !== undefined,
		);
		return written ? fields : null;
	}

	/** De dónde llega quien rellena el formulario. */
	private origin(): 'web' | 'platform' {
		return this.route.snapshot.queryParamMap.get('from') === 'platform' ? 'platform' : 'web';
	}

	// El almacenamiento del navegador puede estar prohibido (modo privado, ajustes
	// del propio navegador). Es una comodidad y no una pieza del circuito: si
	// falla, la reanudación sigue viva por el enlace del correo.
	private storedDraftToken(): string | null {
		try {
			return localStorage.getItem(DRAFT_STORAGE_KEY);
		} catch {
			return null;
		}
	}

	private rememberDraftToken(token: string): void {
		try {
			localStorage.setItem(DRAFT_STORAGE_KEY, token);
		} catch {
			/* sin almacenamiento se sigue igual */
		}
	}

	private forgetDraftToken(): void {
		try {
			localStorage.removeItem(DRAFT_STORAGE_KEY);
		} catch {
			/* nada que olvidar */
		}
	}

	invalid(control: keyof typeof this.form.controls): boolean {
		const field = this.form.controls[control];
		return field.invalid && field.touched;
	}
}
